// Hatchery v0 (findings #84): a lot that arrives as eyed eggs.
//
// Until first feeding the lot does not eat or grow, so the growth / feed
// projections (lotProjection.ts) do not apply. What matters is TUA
// (unidades térmicas acumuladas, °C·día), density and water flow per unit,
// and survival to hatch and to first feeding.
//
// Everything lives in `metadata` (no migration):
// - `Lot.metadata.lifeStage`          'eggs' | 'yolkSac' | 'firstFeeding' (absent = a fish lot, as before)
// - `Lot.metadata.lifeStageHistory`   [{ stage, date, recordedAt }] — the dates the office recorded
// - `Lot.metadata.hatchery`           arrival record (eggs, TUA at arrival, egg size, health, disinfection, TUA targets)
// - `Lot.metadata.seedSupplier` / `strain`  the existing supplier and strain keys (findings #78)
// - `Farm.metadata.hatcheryDefaults`  { hatchTargetTua, firstFeedingTargetTua, fryWeightFactor,
//                                       and the daily mortality reference (findings #194) }
// - `WaterLog.metadata.flowLpm`       a flow reading (L/min) on the unit's water record
//
// Standards (verified 2026-09-29, see findings #84): GLOBALG.A.P. IFA GFS AQ
// v6.0 18.02.01 (hatching conditions), 20.01.01/.02/.05, 20.02.05, 20.02.13,
// 20.05.02 (mortality % per production stage); BAP Hatchery 2.1 3.50, 4.7,
// 4.27, 4.28, T3, T4; ASC Freshwater Trout v1.2 P7; ASC Farm Standard 1.5.2.
// Local practice: FONDEPES Manual de Cultivo de Trucha, Formato 01 (Hoja de
// recepción de ovas embrionadas).
//
// Pure: callers inject every input.

import {
  addDays,
  daysBetween,
  degreeDays,
  meanRecentTemperature,
  normalizeSeries,
  type DatedValue
} from './lotProjection'

export type LotLifeStage = 'eggs' | 'yolkSac' | 'firstFeeding'
export const LOT_LIFE_STAGES: LotLifeStage[] = ['eggs', 'yolkSac', 'firstFeeding']

export type LifeStageChange = { stage: LotLifeStage; date: string; recordedAt: string }

export type StockTrait = 'allFemale' | 'triploid'
export const STOCK_TRAITS: StockTrait[] = ['allFemale', 'triploid']

export type EggArrival = {
  eggsReceived?: number
  tuaAtArrival?: number
  eggDiameterMm?: number
  /** Eggs in a 12-inch (30.48 cm) trough — the Von Bayer count; gives the diameter (findings #112). */
  vonBayerCount?: number
  eggsPerGram?: number
  /** Von Bayer eggs per litre and the total litres of eggs (FONDEPES Formato 01). */
  eggsPerLitre?: number
  eggVolumeLitres?: number
  /** True when the eggs received were estimated as eggs per litre × litres (BAP Hatchery 2.1 3.50). */
  countFromVolume?: boolean
  supplierLot?: string
  supplierCertification?: string
  /** Broodstock group / origin from the packing list (findings #151): a supplier runs several groups to cover the year. */
  broodstockGroup?: string
  /** Spawning (fertilisation) date from the packing list, YYYY-MM-DD. */
  spawningDate?: string
  stockTraits?: StockTrait[]
  healthCertificateNo?: string
  healthCertificateDate?: string
  quarantine?: boolean
  disinfectionProduct?: string
  disinfectionPpm?: number
  disinfectionMinutes?: number
  disinfectionDate?: string
  hatchTargetTua?: number
  firstFeedingTargetTua?: number
}

export type HatcheryDefaults = {
  hatchTargetTua: number | null
  firstFeedingTargetTua: number | null
  /** Fry weight at first feeding ÷ egg weight. 1.0 when not set. */
  fryWeightFactor: number | null
  /** Daily mortality % reference (findings #194): green up to, red from. Empty = the defaults below. */
  eggMortalityGreenPct?: number | null
  eggMortalityRedPct?: number | null
  yolkSacMortalityGreenPct?: number | null
  yolkSacMortalityRedPct?: number | null
  criticalMortalityGreenPct?: number | null
  criticalMortalityRedPct?: number | null
}

export type MortalityReferenceKind = 'eggs' | 'yolkSac' | 'critical'
export type MortalityReference = { greenMaxPct: number; redFromPct: number }

/**
 * Findings #194: starting daily mortality references (the office can change
 * them in Editar instalación). Eggs: the trout manual expects no more than
 * 1–2 % dead the day after the eggs arrive; yolk-sac survival of about
 * 90–95 % over the ~25 days to first feeding is ~0.2–0.3 % a day. Critical
 * days (arrival, hatching, first feeding, first transfer) get a wider band.
 */
export const DEFAULT_MORTALITY_REFERENCE: Record<MortalityReferenceKind, MortalityReference> = {
  eggs: { greenMaxPct: 1, redFromPct: 2 },
  yolkSac: { greenMaxPct: 0.25, redFromPct: 0.5 },
  critical: { greenMaxPct: 2, redFromPct: 4 }
}

const REFERENCE_KEYS: Record<MortalityReferenceKind, { green: keyof HatcheryDefaults; red: keyof HatcheryDefaults }> = {
  eggs: { green: 'eggMortalityGreenPct', red: 'eggMortalityRedPct' },
  yolkSac: { green: 'yolkSacMortalityGreenPct', red: 'yolkSacMortalityRedPct' },
  critical: { green: 'criticalMortalityGreenPct', red: 'criticalMortalityRedPct' }
}

/** The facility's reference for a kind of day, falling back to the defaults. */
export const mortalityReference = (defaults: HatcheryDefaults, kind: MortalityReferenceKind): MortalityReference => {
  const keys = REFERENCE_KEYS[kind]
  const green = defaults[keys.green]
  const red = defaults[keys.red]
  const greenMaxPct = typeof green === 'number' ? green : DEFAULT_MORTALITY_REFERENCE[kind].greenMaxPct
  const redFromPct = typeof red === 'number' ? red : DEFAULT_MORTALITY_REFERENCE[kind].redFromPct
  return redFromPct > greenMaxPct ? { greenMaxPct, redFromPct } : DEFAULT_MORTALITY_REFERENCE[kind]
}

export const DEFAULT_FRY_WEIGHT_FACTOR = 1

type WithMetadata = { metadata?: Record<string, unknown> | null } | null | undefined

const readMetadata = (value: WithMetadata): Record<string, unknown> => {
  const metadata = value?.metadata
  return metadata && typeof metadata === 'object' && !Array.isArray(metadata) ? metadata : {}
}

const isPlainObject = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
)

const positive = (value: unknown): number | undefined => {
  const parsed = typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value.trim().replace(',', '.')) : NaN
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
}

const nonNegative = (value: unknown): number | undefined => {
  const parsed = typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value.trim().replace(',', '.')) : NaN
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined
}

const text = (value: unknown): string | undefined => (
  typeof value === 'string' && value.trim() ? value.trim() : undefined
)

const isoDay = (value: unknown): string | undefined => {
  const day = typeof value === 'string' ? value.slice(0, 10) : ''
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : undefined
}

const isLifeStage = (value: unknown): value is LotLifeStage => (
  typeof value === 'string' && (LOT_LIFE_STAGES as string[]).includes(value)
)

// ---------------------------------------------------------------- life stage

export const readLifeStage = (lot: WithMetadata): LotLifeStage | null => {
  const stage = readMetadata(lot).lifeStage
  return isLifeStage(stage) ? stage : null
}

/** Eggs and yolk-sac alevins: no feeding, no growth, no weighing. */
export const isHatcheryStage = (stage: LotLifeStage | null | undefined): boolean => (
  stage === 'eggs' || stage === 'yolkSac'
)

export const isHatcheryLot = (lot: WithMetadata): boolean => isHatcheryStage(readLifeStage(lot))

export const readLifeStageHistory = (lot: WithMetadata): LifeStageChange[] => {
  const raw = readMetadata(lot).lifeStageHistory
  if (!Array.isArray(raw)) return []
  return raw
    .filter(isPlainObject)
    .flatMap((entry) => {
      const date = isoDay(entry.date)
      if (!isLifeStage(entry.stage) || !date) return []
      return [{ stage: entry.stage, date, recordedAt: typeof entry.recordedAt === 'string' ? entry.recordedAt : '' }]
    })
}

/** The date a stage was entered, from the lot's stage history. */
export const stageDate = (lot: WithMetadata, stage: LotLifeStage): string | null => {
  const entry = readLifeStageHistory(lot).filter((change) => change.stage === stage).pop()
  return entry?.date ?? null
}

/**
 * Findings #194: the lot's life stage on a day (YYYY-MM-DD), from its stage
 * history: the last change on or before that day. A lot with no history uses
 * its current stage; a day before the first change uses the first stage.
 */
export const lifeStageOn = (lot: WithMetadata, day: string): LotLifeStage | null => {
  const history = [...readLifeStageHistory(lot)].sort((a, b) => a.date.localeCompare(b.date))
  if (history.length === 0) return readLifeStage(lot)
  let stage = history[0].stage
  for (const change of history) {
    if (change.date <= day) stage = change.stage
  }
  return stage
}

export type CriticalDay = 'arrival' | 'hatch' | 'firstFeeding' | 'firstTransfer'

/** Days each critical window lasts, counting the recorded date (findings #194, user choice 2026-10-22). */
export const CRITICAL_DAY_WINDOWS: Record<CriticalDay, number> = {
  arrival: 2,
  hatch: 4,
  firstFeeding: 7,
  firstTransfer: 2
}

/**
 * Findings #194: early stages lose more fish on four kinds of day — the eggs'
 * arrival and re-incubation, hatching, first feeding, and the first transfer
 * after hatching. Found from what is already recorded: the stage dates and
 * the lot's first move (`firstTransferDate`, YYYY-MM-DD) after hatching.
 */
export const criticalDayOn = (lot: WithMetadata, day: string, firstTransferDate?: string | null): CriticalDay | null => {
  const starts: Array<[CriticalDay, string | null | undefined]> = [
    ['arrival', stageDate(lot, 'eggs')],
    ['hatch', stageDate(lot, 'yolkSac')],
    ['firstFeeding', stageDate(lot, 'firstFeeding')],
    ['firstTransfer', firstTransferDate]
  ]
  for (const [kind, start] of starts) {
    if (!start) continue
    if (day >= start && day <= addDays(start, CRITICAL_DAY_WINDOWS[kind] - 1)) return kind
  }
  return null
}

/**
 * The first move of the lot's fish to another unit on or after hatching, as
 * a day: a whole-lot move or transfer, or fish joining the same lot elsewhere.
 */
export const firstTransferAfterHatch = (
  lot: WithMetadata & { id?: string },
  events: ReadonlyArray<{ eventType?: string; recordedAt: string; fromProductionUnitId?: string | null; toProductionUnitId?: string | null; sourceLotIds?: string[]; targetLotIds?: string[]; metadata?: Record<string, unknown> | null }>,
  timeZoneDay: (iso: string) => string = (iso) => iso.slice(0, 10)
): string | null => {
  const hatch = stageDate(lot, 'yolkSac')
  if (!hatch) return null
  const lotId = String(lot?.id ?? '')
  const days = events
    .filter((event) => event.eventType === 'move' || event.eventType === 'transfer')
    .filter((event) => !lotId || (event.sourceLotIds ?? []).includes(lotId) || (event.targetLotIds ?? []).includes(lotId))
    .filter((event) => {
      const fishMove = readMetadata(event).fishMove
      const from = isPlainObject(fishMove) ? fishMove.fromProductionUnitId : event.fromProductionUnitId
      const to = isPlainObject(fishMove) ? fishMove.toProductionUnitId : event.toProductionUnitId
      return Boolean(from) && Boolean(to) && from !== to
    })
    .map((event) => timeZoneDay(event.recordedAt))
    .filter((day) => /^\d{4}-\d{2}-\d{2}$/.test(day) && day >= hatch)
    .sort()
  return days[0] ?? null
}

/** The next stage the office can record, or null after first feeding. */
export const nextLifeStage = (stage: LotLifeStage | null): LotLifeStage | null => {
  if (stage === 'eggs') return 'yolkSac'
  if (stage === 'yolkSac') return 'firstFeeding'
  return null
}

/** Records a stage change on `date` (YYYY-MM-DD). The history keeps every change. */
export const withLifeStage = <T extends NonNullable<WithMetadata>>(
  value: T,
  stage: LotLifeStage,
  date: string,
  recordedAt: string
): T => {
  const metadata = { ...readMetadata(value) }
  metadata.lifeStage = stage
  metadata.lifeStageHistory = [...readLifeStageHistory(value), { stage, date: date.slice(0, 10), recordedAt }]
  return { ...value, metadata }
}

// ---------------------------------------------------------------- arrival

export const readEggArrival = (lot: WithMetadata): EggArrival => {
  const raw = readMetadata(lot).hatchery
  if (!isPlainObject(raw)) return {}
  const traits = Array.isArray(raw.stockTraits)
    ? raw.stockTraits.filter((trait): trait is StockTrait => (STOCK_TRAITS as unknown[]).includes(trait))
    : []
  const eggsReceived = positive(raw.eggsReceived)
  const arrival: EggArrival = {
    eggsReceived: eggsReceived !== undefined ? Math.round(eggsReceived) : undefined,
    tuaAtArrival: nonNegative(raw.tuaAtArrival),
    eggDiameterMm: positive(raw.eggDiameterMm),
    vonBayerCount: positive(raw.vonBayerCount),
    eggsPerGram: positive(raw.eggsPerGram),
    eggsPerLitre: positive(raw.eggsPerLitre),
    eggVolumeLitres: positive(raw.eggVolumeLitres),
    countFromVolume: raw.countFromVolume === true ? true : undefined,
    supplierLot: text(raw.supplierLot),
    supplierCertification: text(raw.supplierCertification),
    broodstockGroup: text(raw.broodstockGroup),
    spawningDate: isoDay(raw.spawningDate),
    stockTraits: traits.length > 0 ? traits : undefined,
    healthCertificateNo: text(raw.healthCertificateNo),
    healthCertificateDate: isoDay(raw.healthCertificateDate),
    quarantine: typeof raw.quarantine === 'boolean' ? raw.quarantine : undefined,
    disinfectionProduct: text(raw.disinfectionProduct),
    disinfectionPpm: positive(raw.disinfectionPpm),
    disinfectionMinutes: positive(raw.disinfectionMinutes),
    disinfectionDate: isoDay(raw.disinfectionDate),
    hatchTargetTua: positive(raw.hatchTargetTua),
    firstFeedingTargetTua: positive(raw.firstFeedingTargetTua)
  }
  return Object.fromEntries(Object.entries(arrival).filter(([, entry]) => entry !== undefined)) as EggArrival
}

/**
 * Saves the arrival record plus supplier / strain (the existing keys), and
 * puts the lot at the egg stage when it has no life stage yet.
 */
export const withEggArrival = <T extends NonNullable<WithMetadata>>(
  value: T,
  args: { arrival: EggArrival; supplier?: string; strain?: string; arrivalDate: string; recordedAt: string }
): T => {
  let next: T = value
  if (!readLifeStage(value)) next = withLifeStage(value, 'eggs', args.arrivalDate, args.recordedAt)
  const metadata = { ...readMetadata(next) }
  const cleaned = Object.fromEntries(Object.entries(args.arrival).filter(([, entry]) => (
    entry !== undefined && entry !== '' && !(Array.isArray(entry) && entry.length === 0)
  )))
  metadata.hatchery = cleaned
  if (args.supplier?.trim()) metadata.seedSupplier = args.supplier.trim()
  else delete metadata.seedSupplier
  if (args.strain?.trim()) metadata.strain = args.strain.trim()
  else delete metadata.strain
  return { ...next, metadata }
}

export const readSupplier = (lot: WithMetadata): string | null => text(readMetadata(lot).seedSupplier) ?? null
export const readStrain = (lot: WithMetadata): string | null => text(readMetadata(lot).strain) ?? null

/** Egg diameter (mm) from a Von Bayer count: 304.8 mm (12 in) ÷ eggs in the trough, 2 decimals. */
export const diameterFromVonBayer = (count: number | null | undefined): number | null => (
  typeof count === 'number' && Number.isFinite(count) && count > 0 ? Math.round((304.8 / count) * 100) / 100 : null
)

/** Eggs per litre × litres, rounded — the volumetric estimate. */
export const eggsFromVolume = (arrival: Pick<EggArrival, 'eggsPerLitre' | 'eggVolumeLitres'>): number | null => (
  arrival.eggsPerLitre && arrival.eggVolumeLitres ? Math.round(arrival.eggsPerLitre * arrival.eggVolumeLitres) : null
)

/** Eggs received: the arrival record, else the stocked count ("Sembrar en varias unidades"). */
export const readEggsReceived = (lot: WithMetadata): number | null => {
  const arrival = readEggArrival(lot).eggsReceived
  if (arrival !== undefined) return arrival
  const stocked = positive(readMetadata(lot).stockedFishCount)
  return stocked !== undefined ? Math.round(stocked) : null
}

// ---------------------------------------------------------------- facility defaults

export const readHatcheryDefaults = (farm: WithMetadata): HatcheryDefaults => {
  const raw = readMetadata(farm).hatcheryDefaults
  const source = isPlainObject(raw) ? raw : {}
  return {
    hatchTargetTua: positive(source.hatchTargetTua) ?? null,
    firstFeedingTargetTua: positive(source.firstFeedingTargetTua) ?? null,
    fryWeightFactor: positive(source.fryWeightFactor) ?? null,
    eggMortalityGreenPct: positive(source.eggMortalityGreenPct) ?? null,
    eggMortalityRedPct: positive(source.eggMortalityRedPct) ?? null,
    yolkSacMortalityGreenPct: positive(source.yolkSacMortalityGreenPct) ?? null,
    yolkSacMortalityRedPct: positive(source.yolkSacMortalityRedPct) ?? null,
    criticalMortalityGreenPct: positive(source.criticalMortalityGreenPct) ?? null,
    criticalMortalityRedPct: positive(source.criticalMortalityRedPct) ?? null
  }
}

export const withHatcheryDefaults = <T extends NonNullable<WithMetadata>>(value: T, defaults: HatcheryDefaults): T => {
  const metadata = { ...readMetadata(value) }
  const cleaned = Object.fromEntries(Object.entries(defaults).filter(([, entry]) => entry !== null && entry !== undefined))
  if (Object.keys(cleaned).length === 0) delete metadata.hatcheryDefaults
  else metadata.hatcheryDefaults = cleaned
  return { ...value, metadata }
}

// ---------------------------------------------------------------- egg size

/**
 * Egg weight (mg) from eggs per gram, else from the diameter as a sphere of
 * water density (π/6 · d³; 1 mm³ ≈ 1 mg). An estimate — labelled "aprox.".
 */
export const eggWeightMg = (arrival: Pick<EggArrival, 'eggDiameterMm' | 'eggsPerGram'>): number | null => {
  if (arrival.eggsPerGram && arrival.eggsPerGram > 0) return 1000 / arrival.eggsPerGram
  if (arrival.eggDiameterMm && arrival.eggDiameterMm > 0) return (Math.PI / 6) * arrival.eggDiameterMm ** 3
  return null
}

export const estimatedFryWeightG = (
  arrival: Pick<EggArrival, 'eggDiameterMm' | 'eggsPerGram'>,
  fryWeightFactor: number | null
): number | null => {
  const egg = eggWeightMg(arrival)
  if (egg === null) return null
  return (egg / 1000) * (fryWeightFactor && fryWeightFactor > 0 ? fryWeightFactor : DEFAULT_FRY_WEIGHT_FACTOR)
}

// ---------------------------------------------------------------- volume / flow

/** Unit volume in litres from Editar instalación's capacity (m³ or L). */
export const unitVolumeLitres = (unit: { capacity?: unknown; capacityUnit?: unknown } | null | undefined): number | null => {
  const capacity = positive(unit?.capacity)
  if (capacity === undefined) return null
  const capacityUnit = typeof unit?.capacityUnit === 'string' && unit.capacityUnit ? unit.capacityUnit : 'm3'
  if (capacityUnit === 'L') return capacity
  if (capacityUnit === 'm3') return capacity * 1000
  return null
}

export const readFlowLpm = (row: { metadata?: unknown } | null | undefined): number | null => {
  const metadata = row?.metadata
  if (!isPlainObject(metadata)) return null
  return positive(metadata.flowLpm) ?? null
}

// ---------------------------------------------------------------- the view

export type HatcheryMilestone = {
  targetTua: number | null
  /** Recorded date (the office registered the stage). */
  recordedDate: string | null
  /** TUA reached on the recorded date. */
  tuaAtRecordedDate: number | null
  /** Expected date from TUA and recent temperatures, while not recorded. */
  expectedDate: string | null
  daysToGo: number | null
  /** TUA already at or past the target but the stage is not recorded yet. */
  due: boolean
}

export type HatcheryViewInput = {
  stage: LotLifeStage | null
  arrivalDate: string | null
  arrival: EggArrival
  eggsReceived: number | null
  hatchDate: string | null
  firstFeedingDate: string | null
  defaults: HatcheryDefaults
  /** Water temperature readings (°C) of the lot's unit (with borrowed days, see `withArrivalTemperatures`). */
  temperatures: DatedValue[]
  /** Days whose temperature comes from another unit of the same arrival. */
  borrowedTemperatureDays?: string[]
  /** Days with a mortality record (dead eggs / alevins that day; 0 = "Sin mortalidad"). */
  mortalityRecordedDays: DatedValue[]
  /** Latest flow reading of the unit (L/min), if any. */
  latestFlowLpm: DatedValue | null
  unitVolumeLitres: number | null
  today: string
}

export type HatcheryCaution =
  | 'noArrivalDate'
  | 'noEggCount'
  | 'noTemperature'
  | 'fewTemperatureDays'
  | 'noMortalityRecords'
  | 'lowMortalityCompleteness'
  | 'noTargets'

export type HatcheryView = {
  tuaAtArrival: number
  tuaAccumulated: number | null
  tuaToDate: number | null
  temperatureDaysRecorded: number
  /** Of `temperatureDaysRecorded`, the days read in another unit of the same arrival. */
  temperatureDaysBorrowed: number
  daysSinceArrival: number | null
  meanTemperatureC: number | null
  hatch: HatcheryMilestone
  firstFeeding: HatcheryMilestone
  eggsReceived: number | null
  eggDeaths: number
  alevinDeaths: number
  aliveNow: number | null
  survivalToHatchPct: number | null
  survivalToFirstFeedingPct: number | null
  /** Survival so far while the stage is still running. */
  survivalToDatePct: number | null
  mortalityDaysRecorded: number
  mortalityCompletenessPct: number | null
  perLitre: number | null
  /** Findings #153: the unit volume behind `perLitre`, shown as the formula. */
  unitVolumeLitres: number | null
  flowLpm: number | null
  flowDate: string | null
  flowPerThousandLpm: number | null
  eggWeightMg: number | null
  estimatedFryWeightG: number | null
  cautions: HatcheryCaution[]
}

const LOW_COMPLETENESS_PCT = 70

const round = (value: number | null, digits: number): number | null => (
  value === null || !Number.isFinite(value) ? null : Number(value.toFixed(digits))
)

// The farm measures the water temperature at one point per area (ARFISH:
// one incubator in Potgapampa, 2026-10-09), so a lot sown into several
// incubators has readings in one unit only. For a day without a reading in
// the lot's own unit, TUA uses the readings of the other units of the same
// arrival (same lineage root) that day — their mean when several have one.
// Never from unrelated lots. GLOBALG.A.P. AQ 18.02.01 (hatching conditions).

export type ArrivalTemperatureSource = { unitName: string; temperatures: DatedValue[] }

export type ArrivalTemperatures = {
  temperatures: DatedValue[]
  /** Days filled from other units, sorted. */
  borrowedDays: string[]
  /** Names of the units that filled at least one day, sorted. */
  borrowedFrom: string[]
}

export const withArrivalTemperatures = (
  own: DatedValue[],
  others: ReadonlyArray<ArrivalTemperatureSource>
): ArrivalTemperatures => {
  const ownSeries = normalizeSeries(own, () => true)
  const ownDays = new Set(ownSeries.map((point) => point.date))
  const byDay = new Map<string, { sum: number; count: number; units: Set<string> }>()
  for (const source of others) {
    for (const point of normalizeSeries(source.temperatures, () => true)) {
      if (ownDays.has(point.date)) continue
      const entry = byDay.get(point.date) ?? { sum: 0, count: 0, units: new Set<string>() }
      entry.sum += point.value
      entry.count += 1
      entry.units.add(source.unitName)
      byDay.set(point.date, entry)
    }
  }
  const borrowedFrom = new Set<string>()
  const borrowed: DatedValue[] = []
  byDay.forEach((entry, date) => {
    borrowed.push({ date, value: entry.sum / entry.count })
    entry.units.forEach((unit) => borrowedFrom.add(unit))
  })
  return {
    temperatures: [...ownSeries, ...borrowed].sort((a, b) => a.date.localeCompare(b.date)),
    borrowedDays: borrowed.map((point) => point.date).sort(),
    borrowedFrom: [...borrowedFrom].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
  }
}

/**
 * Findings #217: the unit's measuring point fills the days still without a
 * temperature after the same arrival (#150). The borrowed days and units add up.
 */
export const withMeasuringPointTemperatures = (
  base: ArrivalTemperatures,
  point: ArrivalTemperatureSource | null
): ArrivalTemperatures => {
  if (!point) return base
  const filled = withArrivalTemperatures(base.temperatures, [point])
  if (filled.borrowedDays.length === 0) return base
  return {
    temperatures: filled.temperatures,
    borrowedDays: [...base.borrowedDays, ...filled.borrowedDays].sort(),
    borrowedFrom: [...new Set([...base.borrowedFrom, ...filled.borrowedFrom])].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
  }
}

export const buildHatcheryView = (input: HatcheryViewInput): HatcheryView => {
  const cautions: HatcheryCaution[] = []
  const temps = normalizeSeries(input.temperatures, () => true)
  const deaths = normalizeSeries(input.mortalityRecordedDays, (value) => value >= 0)
  const arrivalDate = input.arrivalDate ? input.arrivalDate.slice(0, 10) : null
  const tuaAtArrival = input.arrival.tuaAtArrival ?? 0
  const today = input.today

  if (!arrivalDate) cautions.push('noArrivalDate')

  // TUA to a day: TUA at arrival + one mean daily temperature per full day
  // since arrival (the day's latest reading, carried forward over gaps).
  const tuaOn = (day: string): number | null => {
    if (!arrivalDate) return null
    if (day <= arrivalDate) return tuaAtArrival
    const sum = degreeDays(temps, arrivalDate, day)
    return sum === null ? null : tuaAtArrival + sum
  }

  const daysSinceArrival = arrivalDate ? Math.max(0, daysBetween(arrivalDate, today)) : null
  const tuaToDate = arrivalDate ? tuaOn(today) : null
  if (arrivalDate && temps.length === 0) cautions.push('noTemperature')
  const temperatureDaysRecorded = arrivalDate
    ? temps.filter((point) => point.date >= arrivalDate && point.date < today).length
    : 0
  const borrowed = new Set(input.borrowedTemperatureDays ?? [])
  const temperatureDaysBorrowed = arrivalDate
    ? temps.filter((point) => point.date >= arrivalDate && point.date < today && borrowed.has(point.date)).length
    : 0
  if (daysSinceArrival && daysSinceArrival > 0 && temps.length > 0
    && (temperatureDaysRecorded / daysSinceArrival) * 100 < LOW_COMPLETENESS_PCT) {
    cautions.push('fewTemperatureDays')
  }
  const meanTemperature = meanRecentTemperature(temps, today)

  const hatchTarget = input.arrival.hatchTargetTua ?? input.defaults.hatchTargetTua
  const firstFeedingTarget = input.arrival.firstFeedingTargetTua ?? input.defaults.firstFeedingTargetTua
  if (hatchTarget === null && firstFeedingTarget === null) cautions.push('noTargets')

  const milestone = (targetTua: number | null, recordedDate: string | null): HatcheryMilestone => {
    const base: HatcheryMilestone = {
      targetTua,
      recordedDate,
      tuaAtRecordedDate: recordedDate ? round(tuaOn(recordedDate), 0) : null,
      expectedDate: null,
      daysToGo: null,
      due: false
    }
    if (recordedDate || targetTua === null || tuaToDate === null) return base
    if (tuaToDate >= targetTua) return { ...base, due: true }
    if (meanTemperature === null || !(meanTemperature > 0)) return base
    const days = Math.ceil((targetTua - tuaToDate) / meanTemperature)
    return { ...base, expectedDate: addDays(today, days), daysToGo: days }
  }

  // Deaths split by stage: before the hatch date they are eggs, from hatch
  // to first feeding they are yolk-sac alevins (GLOBALG.A.P. 20.05.02:
  // mortality percentage per production stage).
  const inWindow = (day: string, from: string | null, to: string | null): boolean => (
    (!from || day >= from) && (!to || day < to)
  )
  const eggDeaths = deaths
    .filter((point) => inWindow(point.date, arrivalDate, input.hatchDate))
    .reduce((sum, point) => sum + point.value, 0)
  const alevinDeaths = input.hatchDate
    ? deaths
      .filter((point) => inWindow(point.date, input.hatchDate, input.firstFeedingDate))
      .reduce((sum, point) => sum + point.value, 0)
    : 0

  const received = input.eggsReceived && input.eggsReceived > 0 ? input.eggsReceived : null
  if (received === null) cautions.push('noEggCount')
  const pct = (alive: number): number | null => (received ? round((alive / received) * 100, 1) : null)
  const aliveNow = received !== null ? Math.max(0, received - eggDeaths - alevinDeaths) : null

  const stageEnd = input.firstFeedingDate ?? today
  const recordedInStage = arrivalDate
    ? deaths.filter((point) => point.date >= arrivalDate && point.date <= stageEnd).length
    : deaths.length
  const stageDays = arrivalDate ? Math.max(1, daysBetween(arrivalDate, stageEnd) + (input.firstFeedingDate ? 0 : 1)) : null
  const completeness = stageDays ? round((recordedInStage / stageDays) * 100, 1) : null
  if (arrivalDate) {
    if (recordedInStage === 0) cautions.push('noMortalityRecords')
    else if (completeness !== null && completeness < LOW_COMPLETENESS_PCT) cautions.push('lowMortalityCompleteness')
  }

  const flowLpm = input.latestFlowLpm && input.latestFlowLpm.value > 0 ? input.latestFlowLpm.value : null
  const egg = eggWeightMg(input.arrival)
  return {
    tuaAtArrival,
    tuaAccumulated: tuaToDate === null ? null : round(tuaToDate - tuaAtArrival, 0),
    tuaToDate: round(tuaToDate, 0),
    temperatureDaysRecorded,
    temperatureDaysBorrowed,
    daysSinceArrival,
    meanTemperatureC: round(meanTemperature, 1),
    hatch: milestone(hatchTarget, input.hatchDate),
    firstFeeding: milestone(firstFeedingTarget, input.firstFeedingDate),
    eggsReceived: received,
    eggDeaths,
    alevinDeaths,
    aliveNow,
    survivalToHatchPct: input.hatchDate && received ? pct(received - eggDeaths) : null,
    survivalToFirstFeedingPct: input.firstFeedingDate && received ? pct(received - eggDeaths - alevinDeaths) : null,
    survivalToDatePct: aliveNow !== null ? pct(aliveNow) : null,
    mortalityDaysRecorded: recordedInStage,
    mortalityCompletenessPct: completeness,
    perLitre: aliveNow !== null && input.unitVolumeLitres ? round(aliveNow / input.unitVolumeLitres, 1) : null,
    unitVolumeLitres: input.unitVolumeLitres || null,
    flowLpm: round(flowLpm, 2),
    flowDate: input.latestFlowLpm?.date ?? null,
    flowPerThousandLpm: flowLpm !== null && aliveNow ? round(flowLpm / (aliveNow / 1000), 3) : null,
    eggWeightMg: round(egg, 1),
    estimatedFryWeightG: round(estimatedFryWeightG(input.arrival, input.defaults.fryWeightFactor), 3),
    cautions
  }
}
